/* eslint-disable react-refresh/only-export-components --
 * I18n module intentionally co-locates the provider, hook, and TranslationKey type.
 * The cost is HMR-only: editing this file forces a full reload instead of a hot patch.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { Language } from '@/types';

type TranslationKey =
  | 'nav.home' | 'nav.events' | 'nav.archive' | 'nav.artists' | 'nav.shop' | 'nav.contact'
  | 'cta.tickets' | 'cta.buy' | 'cta.add' | 'cta.checkout' | 'cta.continue' | 'cta.confirm'
  | 'cta.back' | 'cta.viewAll' | 'cta.lineup' | 'cta.details' | 'cta.signup' | 'cta.request'
  | 'label.next' | 'label.upcoming' | 'label.archive' | 'label.artists' | 'label.featured'
  | 'label.shop' | 'label.contact' | 'label.lineup' | 'label.tier' | 'label.qty' | 'label.total'
  | 'label.subtotal' | 'label.fee' | 'label.email' | 'label.name' | 'label.phone' | 'label.message'
  | 'label.subject' | 'label.address' | 'label.coords' | 'label.doors' | 'label.close'
  | 'label.capacity' | 'label.sold' | 'label.remaining' | 'label.state' | 'label.code'
  | 'label.year' | 'label.all' | 'label.venue' | 'label.date' | 'label.role' | 'label.since'
  | 'label.tag' | 'label.booking' | 'label.manager' | 'label.played' | 'label.signature'
  | 'label.links' | 'state.available' | 'state.low' | 'state.soldout' | 'state.waitlist'
  | 'foot.signup' | 'foot.signup.note' | 'home.hero.l1' | 'home.hero.l2' | 'home.tag'
  | 'home.intro' | 'checkout.payment' | 'checkout.card' | 'checkout.exp' | 'checkout.cvc'
  | 'checkout.success' | 'checkout.success.note';

type Dictionary = Partial<Record<TranslationKey, string>>;

const STRINGS: Record<Language, Dictionary> = {
  en: {
    'nav.home': 'INDEX',
    'nav.events': 'EVENTS',
    'nav.archive': 'ARCHIVE',
    'nav.artists': 'ARTISTS',
    'nav.shop': 'SHOP',
    'nav.contact': 'CONTACT',
    'cta.tickets': 'TICKETS',
    'cta.buy': 'BUY',
    'cta.add': 'ADD',
    'cta.checkout': 'CHECKOUT',
    'cta.continue': 'CONTINUE',
    'cta.confirm': 'CONFIRM',
    'cta.back': 'BACK',
    'cta.viewAll': 'VIEW ALL',
    'cta.lineup': 'LINEUP',
    'cta.details': 'DETAILS',
    'cta.signup': 'SUBSCRIBE',
    'cta.request': 'REQUEST BOOKING',
    'label.next': 'NEXT EVENT',
    'label.upcoming': 'UPCOMING',
    'label.archive': 'ARCHIVE',
    'label.artists': 'ARTISTS / RESIDENTS',
    'label.featured': 'FEATURED GUESTS',
    'label.shop': 'SHOP / 7Y',
    'label.contact': 'CONTACT / BOOKING',
    'label.lineup': 'LINEUP',
    'label.tier': 'TIER',
    'label.qty': 'QTY',
    'label.total': 'TOTAL',
    'label.subtotal': 'SUBTOTAL',
    'label.fee': 'BOOKING FEE',
    'label.email': 'EMAIL',
    'label.name': 'NAME',
    'label.phone': 'PHONE',
    'label.message': 'MESSAGE',
    'label.subject': 'SUBJECT',
    'label.address': 'ADDRESS',
    'label.coords': 'COORDS',
    'label.doors': 'DOORS',
    'label.close': 'CLOSE',
    'label.capacity': 'CAPACITY',
    'label.sold': 'SOLD',
    'label.remaining': 'REMAINING',
    'label.state': 'STATE',
    'label.code': 'CODE',
    'label.year': 'YEAR',
    'label.all': 'ALL',
    'label.venue': 'VENUE',
    'label.date': 'DATE',
    'label.role': 'ROLE',
    'label.since': 'SINCE',
    'label.tag': 'TAG',
    'label.booking': 'BOOKING',
    'label.manager': 'BOOKED VIA',
    'label.played': 'PLAYED AT MOCT',
    'label.signature': 'SIGNATURE',
    'label.links': 'LINKS',
    'state.available': 'AVAILABLE',
    'state.low': 'LOW STOCK',
    'state.soldout': 'SOLD OUT',
    'state.waitlist': 'WAITLIST',
    'foot.signup': 'JOIN THE SIGNAL',
    'foot.signup.note': 'Lineups, drops, ticket waves. Low frequency mailer.',
    'home.hero.l1': 'MOCT',
    'home.hero.l2': 'IN FREQUENCY',
    'home.tag': 'YEREVAN — UNDERGROUND CLUB SYSTEM. SINCE 2019.',
    'home.intro':
      'A collective of techno labels, experimental practice, and contemporary art operating between Hayfilm Cluster and Ayrarat Cinema in Yerevan.',
    'checkout.payment': 'PAYMENT',
    'checkout.card': 'CARD NUMBER',
    'checkout.exp': 'EXP',
    'checkout.cvc': 'CVC',
    'checkout.success': 'TRANSMISSION RECEIVED',
    'checkout.success.note':
      'Tickets dispatched to your inbox. Bring ID. 18+. Face control enforced.',
  },
  hy: {
    'nav.home': 'ԻՆԴԵՔՍ',
    'nav.events': 'ԻՐԱԴԱՐՁՈՒԹՅՈՒՆՆԵՐ',
    'nav.archive': 'ԱՐԽԻՎ',
    'nav.artists': 'ԱՐՏԻՍՏՆԵՐ',
    'nav.shop': 'ԽԱՆՈՒԹ',
    'nav.contact': 'ԿԱՊ',
    'cta.tickets': 'ՏՈՄՍԵՐ',
    'cta.buy': 'ԳՆԵԼ',
    'cta.add': 'ԱՎԵԼԱՑՆԵԼ',
    'cta.checkout': 'ՎՃԱՐՈՒՄ',
    'cta.continue': 'ՇԱՐՈՒՆԱԿԵԼ',
    'cta.confirm': 'ՀԱՍՏԱՏԵԼ',
    'cta.back': 'ԵՏ',
    'cta.viewAll': 'ՏԵՍՆԵԼ ԲՈԼՈՐԸ',
    'cta.lineup': 'ԼԱՅՆԱՓ',
    'cta.details': 'ՄԱՆՐԱՄԱՍՆ',
    'cta.signup': 'ԲԱԺԱՆՈՐԴԱԳՐՎԵԼ',
    'label.next': 'ՀԱՋՈՐԴ',
    'label.upcoming': 'ԱՌԱՋԻԿԱ',
    'label.archive': 'ԱՐԽԻՎ',
    'label.artists': 'ԱՐՏԻՍՏՆԵՐ',
    'label.featured': 'ՀՅՈՒՐԵՐ',
    'label.shop': 'ԽԱՆՈՒԹ / 7Y',
    'label.contact': 'ԿԱՊ',
    'label.lineup': 'ԼԱՅՆԱՓ',
    'label.tier': 'ՏՈՄՍ',
    'label.qty': 'ՔԱՆ',
    'label.total': 'ԸՆԴԱՄԵՆԸ',
    'label.subtotal': 'ԵՆԹԱԸՆԴ.',
    'label.fee': 'ՎՃԱՐ',
    'label.email': 'ԷԼ. ՓՈՍՏ',
    'label.name': 'ԱՆՈՒՆ',
    'label.phone': 'ՀԵՌ',
    'label.message': 'ՀԱՂՈՐԴԱԳՐ',
    'label.subject': 'ԹԵՄԱ',
    'label.address': 'ՀԱՍՑԵ',
    'label.coords': 'ԿՈՈՐԴ',
    'label.doors': 'ԴՌՆԵՐ',
    'label.close': 'ՓԱԿՈՒՄ',
    'label.capacity': 'ՏԱՐՈՂ',
    'label.sold': 'ՎԱՃԱՌՎԱԾ',
    'label.remaining': 'ՄՆԱՑԱԾ',
    'label.state': 'ՎԻՃԱԿ',
    'label.code': 'ԿՈԴ',
    'label.year': 'ՏԱՐԻ',
    'label.all': 'ԲՈԼՈՐԸ',
    'label.venue': 'ՎԱՅՐ',
    'label.date': 'ԱՄՍԱԹԻՎ',
    'label.role': 'ԴԵՐ',
    'label.since': 'ՍԿՍԱԾ',
    'label.tag': 'ՈՃ',
    'state.available': 'ՀԱՍԱՆԵԼԻ',
    'state.low': 'ՍԱԿԱՎ',
    'state.soldout': 'ՍՊԱՌՎԱԾ',
    'state.waitlist': 'ՍՊԱՍՈՒՄ',
    'foot.signup': 'ՄԻԱՑԻՐ ՀԱՃԱԽՈՒԹՅԱՆԸ',
    'foot.signup.note': 'Լայնափներ, թողարկումներ, տոմսերի ալիքներ:',
    'home.hero.l1': 'MOCT',
    'home.hero.l2': 'IN FREQUENCY',
    'home.tag': 'ԵՐԵՎԱՆ — ԱՆԴԵՐԳՐՈՒՆԴ ԿԼՈՒԲ ՀԱՄԱԿԱՐԳ. 2019-ԻՑ:',
    'home.intro':
      'Տեխնո լեյբլների, փորձարարական պրակտիկայի և ժամանակակից արվեստի կոլեկտիվ՝ Hayfilm Cluster-ի և Ayrarat Cinema-ի միջև:',
    'checkout.payment': 'ՎՃԱՐՈՒՄ',
    'checkout.card': 'ՔԱՐՏԻ ՀԱՄԱՐ',
    'checkout.exp': 'ԺԱՄԿԵՏ',
    'checkout.cvc': 'CVC',
    'checkout.success': 'ՀԱՂՈՐԴՈՒՄԸ ՍՏԱՑՎԱԾ Է',
    'checkout.success.note': 'Տոմսերը ուղարկված են: 18+: Face control:',
  },
  ru: {
    'nav.home': 'ИНДЕКС',
    'nav.events': 'СОБЫТИЯ',
    'nav.archive': 'АРХИВ',
    'nav.artists': 'АРТИСТЫ',
    'nav.shop': 'МАГАЗИН',
    'nav.contact': 'КОНТАКТ',
    'cta.tickets': 'БИЛЕТЫ',
    'cta.buy': 'КУПИТЬ',
    'cta.add': 'ДОБАВИТЬ',
    'cta.checkout': 'ОПЛАТА',
    'cta.continue': 'ДАЛЕЕ',
    'cta.confirm': 'ПОДТВЕРД.',
    'cta.back': 'НАЗАД',
    'cta.viewAll': 'ВСЁ',
    'cta.lineup': 'ЛАЙНАП',
    'cta.details': 'ПОДРОБНЕЕ',
    'cta.signup': 'ПОДПИСАТЬСЯ',
    'label.next': 'СЛЕДУЮЩЕЕ',
    'label.upcoming': 'СКОРО',
    'label.archive': 'АРХИВ',
    'label.artists': 'АРТИСТЫ / РЕЗИДЕНТЫ',
    'label.featured': 'ГОСТИ',
    'label.shop': 'МАГАЗИН / 7Y',
    'label.contact': 'КОНТАКТ',
    'label.lineup': 'ЛАЙНАП',
    'label.tier': 'ВОЛНА',
    'label.qty': 'КОЛ.',
    'label.total': 'ИТОГО',
    'label.subtotal': 'СУММА',
    'label.fee': 'СБОР',
    'label.email': 'EMAIL',
    'label.name': 'ИМЯ',
    'label.phone': 'ТЕЛ',
    'label.message': 'СООБЩ',
    'label.subject': 'ТЕМА',
    'label.address': 'АДРЕС',
    'label.coords': 'КООРД',
    'label.doors': 'ДВЕРИ',
    'label.close': 'ЗАКРЫТИЕ',
    'label.capacity': 'ВМЕСТ.',
    'label.sold': 'ПРОДАНО',
    'label.remaining': 'ОСТАЛОСЬ',
    'label.state': 'СТАТУС',
    'label.code': 'КОД',
    'label.year': 'ГОД',
    'label.all': 'ВСЁ',
    'label.venue': 'ПЛОЩАДКА',
    'label.date': 'ДАТА',
    'label.role': 'РОЛЬ',
    'label.since': 'C',
    'label.tag': 'ТЕГ',
    'state.available': 'ДОСТУПНО',
    'state.low': 'МАЛО',
    'state.soldout': 'НЕТ',
    'state.waitlist': 'ЛИСТ',
    'foot.signup': 'ВОЙТИ В СИГНАЛ',
    'foot.signup.note': 'Лайнапы, релизы, волны билетов.',
    'home.hero.l1': 'MOCT',
    'home.hero.l2': 'IN FREQUENCY',
    'home.tag': 'ЕРЕВАН — ПОДЗЕМНАЯ КЛУБ СИСТЕМА. С 2019.',
    'home.intro':
      'Коллектив техно-лейблов, экспериментальной практики и современного искусства между Hayfilm Cluster и Ayrarat Cinema в Ереване.',
    'checkout.payment': 'ОПЛАТА',
    'checkout.card': 'НОМЕР КАРТЫ',
    'checkout.exp': 'СРОК',
    'checkout.cvc': 'CVC',
    'checkout.success': 'СИГНАЛ ПРИНЯТ',
    'checkout.success.note': 'Билеты отправлены. 18+. Face control.',
  },
};

const LANG_STORAGE_KEY = 'moct.lang';

interface I18nContextValue {
  lang: Language;
  setLang: (lang: Language) => void;
  cycle: () => void;
  t: (key: TranslationKey) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function readStoredLang(): Language {
  try {
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    if (stored === 'en' || stored === 'hy' || stored === 'ru') return stored;
  } catch {
    /* localStorage unavailable */
  }
  return 'en';
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Language>(readStoredLang);

  useEffect(() => {
    try {
      localStorage.setItem(LANG_STORAGE_KEY, lang);
    } catch {
      /* localStorage unavailable */
    }
    document.documentElement.lang = lang === 'hy' ? 'hy' : lang;
  }, [lang]);

  const t = useCallback(
    (key: TranslationKey): string => STRINGS[lang][key] ?? STRINGS.en[key] ?? key,
    [lang],
  );

  const cycle = useCallback(() => {
    setLang((l) => (l === 'en' ? 'hy' : l === 'hy' ? 'ru' : 'en'));
  }, []);

  const value = useMemo<I18nContextValue>(
    () => ({ lang, setLang, cycle, t }),
    [lang, t, cycle],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}

export type { TranslationKey };
